import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { DESCRIPTION, fieldTypes } from '@/helpers/constants';
import { formatDate } from '@/helpers/utils';
import useCampaignSearchSelected from '@/hooks/useCampaignSearchSelected';
import { setCampaignSearchSelectedCoincidendeFieldId, setHighlightedTab } from '@/redux/Campaign/campaign.action';
import { getCampaignInfo, getCampaignSearch } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { CampaignSearch } from '@/types';
import styles from './HighlightMask.module.scss';

type HighlightMaskProps = {
  fieldId: string;
  fieldPath: string[];
  children: React.ReactElement;
  campaignSearch: CampaignSearch;
  isCellInput?: boolean;
  isUserInput?: boolean;
  value: string;
  fieldType?: string;
  disabled: boolean;
  isCampaignSearchPopUpInput?: boolean;
  noTranslationField?: boolean;
  isSeriesDropdown?: boolean;
  setSelectedFieldId: (fieldId: string) => void;
  setHighlightedTab: (tab: string | { plant: string; tab: string }) => void;
};

const HighlightMask: React.FC<HighlightMaskProps> = (props) => {
  const { fieldId, fieldPath, children, campaignSearch, isCellInput = false, isUserInput = false, value,
    fieldType = null, disabled, isCampaignSearchPopUpInput = false, noTranslationField = false, isSeriesDropdown = false,
    setSelectedFieldId, setHighlightedTab } = props;
  const { campaignSearchSelected } = useCampaignSearchSelected(fieldPath, fieldType, noTranslationField);

  const highlightsRef = useRef<HTMLElement | null>(null);
  const backdropRef = useRef<HTMLElement | null>(null);
  const fieldRef = useRef<HTMLElement | null>(null);
  const isCalendarInput = fieldType === fieldTypes.CALENDAR;
  const path = campaignSearch?.fieldSelected?.field?.path;

  const problemsTabSelection = () => {
    const fieldPath = path.split(DESCRIPTION.PROBLEMS)[1].trim().split('.');
    setHighlightedTab(fieldPath[0].replace(/\[|\]/g, ''));
  };

  const limitationVolumeSelection = (result) => {
    const fieldPath = path.split('.')[1];
    const codeRules = 'codeRules';
    const isCodeRules = path.includes(codeRules);
    setHighlightedTab({ plant: result.plant, tab: isCodeRules ? codeRules : fieldPath });
  };
  // If we found a result in some field, then set de element id in redux to retrieve it on the CR detail and make automatic
  // scroll to it
  useEffect(() => {
    if (campaignSearch?.active && campaignSearch.resultCounter > 0 && campaignSearchSelected
      && fieldId !== campaignSearch.selectedCoincidenceFieldId
    ) {
      setSelectedFieldId(fieldId);
    }

    if (path?.includes(DESCRIPTION.PROBLEMS)) {
      campaignSearch.results.find((result) => {
        if (result.field.path === path) {
          problemsTabSelection();
        }
      });
    }

    if (path?.includes('limitationVolumeManualEntry') && campaignSearch?.active) {
      campaignSearch.results.find((result) => {
        if (result.field.path === path) {
          limitationVolumeSelection(result);
        }
      });
    }
  }, [campaignSearchSelected, path]);

  const applyHighlights = (string) => {
    if (string === '') return string;

    let text = string;
    if (fieldType === fieldTypes.CALENDAR) text = formatDate(string);
    const formattedText = text?.toString().replace(/\n$/g, '\n\n');
    const regex = new RegExp(campaignSearch.searchedText, 'gi');

    const highlightedText = formattedText?.replace(regex, (match) => (campaignSearchSelected
      ? `<mark style="background-color: orange !important">${match}</mark>`
      : `<mark>${match}</mark>`));

    return highlightedText;
  };

  const handleScroll = () => {
    const { scrollTop, scrollLeft } = fieldRef.current as unknown as HTMLElement;

    if (backdropRef.current) {
      (backdropRef.current as HTMLElement).scrollTop = scrollTop;
      (backdropRef.current as HTMLElement).scrollLeft = scrollLeft;
    }
  };

  useEffect(() => {
    if (campaignSearch?.active && !isCampaignSearchPopUpInput) {
      const highlightedText = applyHighlights(value || '');
      if (highlightsRef.current) (highlightsRef.current as HTMLElement).innerHTML = highlightedText;

      const highlightedField = highlightsRef.current?.querySelector('mark[style*="background-color: orange"]');

      if (highlightedField) {
        (highlightedField as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'end',
        });
      }

      if (fieldRef.current) {
        (fieldRef.current as HTMLElement).addEventListener('scroll', handleScroll);
      }

      if (!highlightedField && location.pathname.includes('feedback')) { setHighlightedTab(campaignSearch.searchedText); }

      return () => {
        if (fieldRef.current) {
          (fieldRef.current as HTMLElement).removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [campaignSearch, value]);

  return (
    <div className={`${styles.Container} ${disabled ? styles.Disabled : ''}`}>
      {
        (campaignSearch?.active)
        && (
          <div
            className={`${isUserInput
              ? styles.BackdropUserInput
              : styles.Backdrop}
                  ${isCalendarInput ? styles.BackdropCalendar : ''}
                  ${isCellInput ? styles.BackdropCell : ''}
                  ${isSeriesDropdown ? styles.BackdropSeriesDropdown : ''}
                  ${!isCalendarInput && !isCellInput && !isUserInput && !isSeriesDropdown ? styles.BackdropGeneral : ''}
                  ${disabled && !isSeriesDropdown ? styles.BackdropDisabled : ''}
                  ${disabled && isSeriesDropdown ? styles.BackdropDisabledSeriesDropdown : ''}`}
            ref={backdropRef}
          >
            <div className={`${isUserInput ? styles.HighlightsUserInput : styles.Highlights}`} ref={highlightsRef} />
          </div>
        )
      }
      {children && React.cloneElement(children, {
        ref: (ref) => { fieldRef.current = ref; },
      })}
    </div>
  );
};

const mapStateToProps = (state) => ({
  campaignSearch: getCampaignSearch(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
});

const actionCreators = {
  setSelectedFieldId: setCampaignSearchSelectedCoincidendeFieldId,
  setHighlightedTab: setHighlightedTab,
};

export default connect(mapStateToProps, actionCreators)(HighlightMask);
